import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Calculator, Phone, MessageCircle } from "lucide-react";

const KARATS = [
  { v: "24kt", l: "Oro 24 carati" },
  { v: "22kt", l: "Oro 22 carati" },
  { v: "21kt", l: "Oro 21 carati" },
  { v: "18kt", l: "Oro 18 carati" },
  { v: "14kt", l: "Oro 14 carati" },
  { v: "9kt", l: "Oro 9 carati" },
  { v: "silver_925", l: "Argento 925" },
];

export default function PreventivoForm() {
  const [form, setForm] = useState({ name: "", phone: "", karat: "18kt", grams: "", note: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const buildWhatsAppLink = (name, phone, karatLabel, grams, note) => {
    const msg =
      `PREVENTIVO da ${name}%0A` +
      `Telefono: ${phone}%0A%0A` +
      `Materiale: ${karatLabel}%0A` +
      `Peso: ${grams} g%0A` +
      (note ? `%0ANote: ${note}%0A` : "") +
      `%0AAttendo il preventivo, grazie.`;
    return `https://wa.me/393389472101?text=${msg}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const selected = KARATS.find((k) => k.v === form.karat);
      const payload = {
        name: form.name,
        phone: form.phone,
        karat: form.karat,
        grams: parseFloat(form.grams),
        note: form.note || null,
      };
      await api.post("/preventivo", payload);
      const waLink = buildWhatsAppLink(form.name, form.phone, selected.l, payload.grams, form.note);
      toast.success("Richiesta inviata! Apriamo WhatsApp per te.");
      window.open(waLink, "_blank");
      setSent(true);
      setForm({ name: "", phone: "", karat: "18kt", grams: "", note: "" });
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o chiama 338 947 2101");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="preventivo-section" className="py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-radial-spotlight" />
      <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-[0.7rem] uppercase tracking-[0.4em] text-[var(--gold)]">— Preventivo Online</span>
            <h2 className="font-serif text-5xl md:text-6xl text-white mt-4 leading-tight">
              Richiedi il tuo <span className="gold-text italic">preventivo.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-6 leading-relaxed">
              Indicaci caratura, peso e numero di telefono: ti ricontattiamo per fornirti la valutazione personalizzata direttamente su WhatsApp o telefonicamente.
            </p>
            <div className="mt-8 luxe-card p-6 border-[var(--gold)]/30">
              <div className="flex items-center gap-3 mb-3">
                <MessageCircle size={20} className="text-[#25D366]" />
                <div className="text-[0.65rem] uppercase tracking-[0.3em] text-[var(--gold)]">Come funziona</div>
              </div>
              <ol className="text-sm text-white/70 space-y-2 list-decimal pl-5">
                <li>Compila il form con caratura, peso e telefono</li>
                <li>Cliccando "Invia" si apre WhatsApp con il messaggio pronto</li>
                <li>Ti rispondiamo con il preventivo personalizzato</li>
              </ol>
              <p className="text-[0.7rem] text-white/40 mt-4 italic">Il prezzo definitivo viene sempre confermato dopo la valutazione tecnica in negozio.</p>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 luxe-card p-8 lg:p-10 space-y-5">
            <div className="flex items-center gap-3 mb-2">
              <Calculator size={20} className="text-[var(--gold)]" />
              <h3 className="font-serif text-2xl text-white">Compila il tuo preventivo</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-white/50">Caratura *</span>
                <select
                  data-testid="prev-karat"
                  required
                  value={form.karat}
                  onChange={(e) => setForm({ ...form, karat: e.target.value })}
                  className="mt-2 w-full bg-[#0a1628] border border-white/15 px-4 py-3 text-white focus:border-[var(--gold)] outline-none"
                >
                  {KARATS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-white/50">Peso in grammi *</span>
                <input
                  data-testid="prev-grams"
                  required type="number" step="0.01" min="0.01"
                  placeholder="es. 25.5"
                  value={form.grams}
                  onChange={(e) => setForm({ ...form, grams: e.target.value })}
                  className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:border-[var(--gold)] outline-none"
                />
              </label>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-white/50">Nome *</span>
                <input
                  data-testid="prev-name"
                  required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 text-white focus:border-[var(--gold)] outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[0.65rem] uppercase tracking-[0.2em] text-white/50">Telefono *</span>
                <input
                  data-testid="prev-phone"
                  required type="tel" placeholder="es. 333 123 4567"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 text-white focus:border-[var(--gold)] outline-none"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-[0.65rem] uppercase tracking-[0.2em] text-white/50">Note (opzionale)</span>
              <textarea
                data-testid="prev-note"
                rows={3} value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Es. tipologia oggetto, presenza pietre, condizione..."
                className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:border-[var(--gold)] outline-none"
              />
            </label>

            <p className="text-xs text-white/40">Cliccando "Invia" si aprirà WhatsApp con la richiesta pronta. Ti ricontattiamo entro 24 ore.</p>

            <div className="flex flex-wrap gap-3">
              <button
                data-testid="prev-submit"
                type="submit"
                disabled={sending}
                className="flex-1 min-w-fit inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] hover:bg-[#1ebe57] disabled:opacity-60 text-white font-semibold text-sm uppercase tracking-widest rounded-sm transition-colors shadow-lg shadow-green-900/30"
              >
                <MessageCircle size={18} /> {sending ? "Invio..." : "Invia Preventivo via WhatsApp"}
              </button>
              <a href="tel:+393389472101" className="btn-ghost-gold">
                <Phone size={14} /> Chiama
              </a>
            </div>

            {sent && (
              <div className="bg-[#25D366]/10 border border-[#25D366]/40 px-4 py-3 text-sm text-[#25D366]">
                ✓ Richiesta inviata. Ti ricontatteremo al più presto con il preventivo personalizzato.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
